import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  CalendarBlank,
  Buildings,
  MapPin,
  GraduationCap,
  CaretLeft,
  CaretRight,
  ClipboardText,
  Hash,
  Briefcase,
  Circle,
  Lock,
  Clock,
  Eye,
  UserSwitch,
  PencilSimple,
} from "@phosphor-icons/react";
import { Loader2, Building2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetMyInterns, getVacancies } from "../../../adapters/Requests";
import ExportDropdown from "../../export";
import useAuth from "../../../hooks/useAuth";
import BuildingAssignmentModal from "../../modals/BuildingAssignmentModal";

export default function SuperviseTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [selectedItType, setSelectedItType] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [uniqueItTypes, setUniqueItTypes] = useState([]);
  const [uniqueBatches, setUniqueBatches] = useState([]);

  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState(false);
  const [selectedInternForBuilding, setSelectedInternForBuilding] =
    useState(null);

  const { data, isLoading, isError, refetch } = useGetMyInterns({
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
    ittype: selectedItType,
    batch: selectedBatch,
    search: searchQuery,
    status: "ASSIGNED",
  });

  const { data: vacanciesData } = getVacancies();

  const canDelegate = auth?.permissions?.includes("assign_supervisor");

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (pagination.pageIndex > 0)
      searchParams.set("page", pagination.pageIndex + 1);
    if (selectedItType) searchParams.set("ittype", selectedItType);
    if (selectedBatch) searchParams.set("batch", selectedBatch);
    if (searchQuery) searchParams.set("search", searchQuery);

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [
    pagination.pageIndex,
    selectedItType,
    selectedBatch,
    searchQuery,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (vacanciesData && vacanciesData.length > 0) {
      setUniqueItTypes(
        Array.from(
          new Set(
            vacanciesData.map((item) =>
              item.vacancy_position.name.toUpperCase()
            )
          )
        )
      );
      setUniqueBatches(
        Array.from(new Set(vacanciesData.map((item) => item.batch.name)))
      );
    }
  }, [vacanciesData]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const handleRowClick = (id) => {
    navigate(`/monthlyassesment/${id}`);
  };

  const handleDelegateClick = (id) => {
    navigate(`/assignsupervisor/${id}`);
  };

  const handleEditBuilding = (intern) => {
    setSelectedInternForBuilding(intern);
    setIsBuildingModalOpen(true);
  };

  const getCurrentMonthYear = () => {
    const months = [
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  };

  const getActionButton = (row) => {
    const today = new Date().getDate();
    const ASSESSMENT_START_DAY = 10;
    const currentMonthStr = getCurrentMonthYear();

    const assessments = row.original.assessments || [];
    const currentAssessment = assessments.find((a) =>
      a.month_year_of_assessment
        ?.toLowerCase()
        .includes(currentMonthStr.toLowerCase())
    );

    return (
      <div className="flex items-center gap-2">
        {canDelegate && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            onClick={(e) => {
              e.stopPropagation();
              handleDelegateClick(row.original.id);
            }}
          >
            <UserSwitch size={14} />
            Delegate
          </Button>
        )}

        {(() => {
          if (today < ASSESSMENT_START_DAY && !currentAssessment) {
            return (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs gap-2 text-slate-400 cursor-not-allowed hover:bg-transparent hover:text-slate-400"
                title={`Assessment opens on the ${ASSESSMENT_START_DAY}th`}
                onClick={(e) => e.stopPropagation()}
              >
                <Lock size={14} />
                Locked
              </Button>
            );
          }

          if (!currentAssessment) {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-2 text-orange-600 border-orange-200 bg-orange-50 hover:bg-orange-100 hover:text-orange-700 hover:border-orange-300"
                title="Intern must submit monthly clearance first"
                onClick={(e) => e.stopPropagation()}
              >
                <Clock size={14} />
                Waiting
              </Button>
            );
          }

          if (
            currentAssessment.punctuality_score !== null &&
            currentAssessment.punctuality_score !== undefined
          ) {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-2 text-green-600 border-green-200 bg-green-50 hover:bg-green-100 hover:text-green-700 hover:border-green-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(row.original.id);
                }}
              >
                <Eye size={14} />
                View
              </Button>
            );
          }

          return (
            <Button
              size="sm"
              variant="default"
              className="h-8 text-xs gap-2 bg-green-600 hover:bg-green-700 text-white shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original.id);
              }}
            >
              <ClipboardText size={14} />
              Assess
            </Button>
          );
        })()}
      </div>
    );
  };

  const columns = [
    {
      header: "Intern Details",
      accessorKey: "fullName",
      cell: ({ row }) => (
        <div className="flex flex-col py-1">
          <span className="font-bold text-gray-900 text-sm">
            {row.original.fullName}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-gray-500 font-mono flex items-center bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
              <Hash size={10} className="mr-0.5" /> {row.original.code}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Education",
      accessorKey: "course",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 max-w-[200px]">
          <div className="flex items-center text-gray-700 text-xs font-semibold truncate">
            <GraduationCap
              className="mr-1.5 text-gray-400 shrink-0"
              size={14}
            />
            <span className="truncate">{row.original.cos}</span>
          </div>
          <div className="flex items-center text-gray-500 text-[11px] truncate">
            <Buildings className="mr-1.5 text-gray-400 shrink-0" size={14} />
            <span className="truncate">{row.original.school}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Placement",
      accessorKey: "businessunit",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          {/* <div className="flex items-center text-gray-700 text-xs font-medium">
            <Briefcase className="mr-1.5 text-gray-400" size={14} />
            {row.original.department || row.original.businessunit}
          </div> */}

          <div
            role="button"
            className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:text-blue-800 hover:underline cursor-pointer mt-1 bg-blue-50 px-1.5 py-0.5 rounded w-fit transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleEditBuilding(row.original);
            }}
            title="Change Office Building"
          >
            <Building2 size={12} />
            <span className="truncate max-w-[100px] font-medium">
              {row.original.buildingName}
            </span>
            <PencilSimple size={12} />
          </div>
          <div className="flex items-center text-gray-500 text-[11px]">
            <MapPin className="mr-1.5 text-gray-400" size={14} />
            {row.original.companylocation}
          </div>
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Circle
            size={8}
            weight="fill"
            className={
              row.original.status === "ACTIVE"
                ? "text-green-500"
                : "text-slate-400"
            }
          />
          <span className="text-xs font-medium text-slate-700 capitalize">
            {row.original.status.toLowerCase().replace("_", " ")}
          </span>
        </div>
      ),
    },
    {
      header: "Attendance",
      accessorKey: "attendance",
      cell: ({ row }) => (
        <div className="flex items-center gap-2 text-xs">
          <div className="flex flex-col items-center px-2 py-1 bg-green-50 rounded border border-green-100">
            <span className="font-bold text-green-700">
              {row.original.attendance}
            </span>
            <span className="text-[9px] text-green-600 uppercase">Present</span>
          </div>
          <div className="text-gray-300">/</div>
          <div className="flex flex-col items-center px-2 py-1 bg-gray-50 rounded border border-gray-100">
            <span className="font-bold text-gray-700">
              {row.original.totaldays}
            </span>
            <span className="text-[9px] text-gray-500 uppercase">Expected</span>
          </div>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => getActionButton(row),
    },
  ];

  const exportColumns = [
    { headerName: "ID", field: "code" },
    { headerName: "Intern Name", field: "fullName" },
    { headerName: "Course", field: "cos" },
    { headerName: "School", field: "school" },
    { headerName: "Department", field: "department" },
    { headerName: "Business Unit", field: "businessunit" },
    { headerName: "Status", field: "status" },
    { headerName: "Days Present", field: "attendance" },
    { headerName: "Expected Days", field: "totaldays" },
  ];

  const tableData = useMemo(() => {
    return (
      data?.interns?.map((intern) => ({
        id: intern.id,
        code: intern.application?.code || "N/A",
        fullName: `${intern.first_name} ${intern.last_name}`,
        status: "ACTIVE",
        ittype:
          intern.application?.vacancy?.vacancy_position?.name?.toUpperCase() ||
          "",
        school:
          intern.application?.ApplicationsAcademicQualification?.institution
            ?.name ||
          intern.application?.ApplicationNYSCDetails?.institution?.name ||
          "",
        batch: intern.application?.vacancy?.batch?.name || "",
        department:
          intern.application?.Applicant?.InternshipProgram?.[0]?.department ||
          "",
        businessunit: intern.application?.vacancy?.business_unit?.name || "",
        attendance: intern.MonthlyAttendances?.[0]?.days_present || 0,
        totaldays: intern.MonthlyAttendances?.[0]?.total_days || 0,
        cos:
          intern.application?.ApplicationsAcademicQualification?.course?.name ||
          intern.application?.ApplicationNYSCDetails?.course?.name ||
          "",
        companylocation: intern.application?.vacancy?.location?.name || "",
        assessments: intern.InternMonthlyAssessments || [],
        buildingName:
          intern.InternPlacements?.[0]?.officeBuilding?.name || "Unassigned",
        buildingId: intern.InternPlacements?.[0]?.office_building_id,
        sbu_csu_code: intern.InternPlacements?.[0]?.sbu_csu_code || intern.application?.vacancy?.business_unit?.code || "",
        businessUnitName: intern.application?.vacancy?.business_unit?.name || "",
      })) || []
    );
  }, [data]);


  const table = useReactTable({
    data: tableData,
    columns,
    pageCount: data?.totalPages || -1,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  if (isError)
    return (
      <div className="p-8 text-center text-red-500">
        An error occurred loading data. Please refresh.
      </div>
    );

  return (
    <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white mt-4">
      <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-dashed border-gray-300"
                >
                  <Funnel className="mr-2 h-3 w-3" />
                  Type: {selectedItType || "All"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-48 max-h-64 overflow-y-auto"
              >
                <DropdownMenuItem onClick={() => setSelectedItType(null)}>
                  {!selectedItType && <Check className="mr-2 h-3 w-3" />} All
                  Types
                </DropdownMenuItem>
                {uniqueItTypes.map((type) => (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => setSelectedItType(type)}
                  >
                    {selectedItType === type && (
                      <Check className="mr-2 h-3 w-3" />
                    )}{" "}
                    {type}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-dashed border-gray-300"
                >
                  <CalendarBlank className="mr-2 h-3 w-3" />
                  Batch: {selectedBatch || "All"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-48 max-h-64 overflow-y-auto"
              >
                <DropdownMenuItem onClick={() => setSelectedBatch(null)}>
                  {!selectedBatch && <Check className="mr-2 h-3 w-3" />} All
                  Batches
                </DropdownMenuItem>
                {uniqueBatches.map((batch) => (
                  <DropdownMenuItem
                    key={batch}
                    onClick={() => setSelectedBatch(batch)}
                  >
                    {selectedBatch === batch && (
                      <Check className="mr-2 h-3 w-3" />
                    )}{" "}
                    {batch}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={14}
              />
              <Input
                placeholder="Search interns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearch}
                className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
              />
            </div>

            <ExportDropdown
              onExport={() => {}}
              data={tableData}
              columns={exportColumns}
              className="h-8"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="hover:bg-transparent border-b border-slate-100"
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-9 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-64 text-center"
                  >
                    <div className="flex justify-center items-center w-full h-full">
                      <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-green-50/30 transition-colors border-b border-slate-100 cursor-pointer group"
                    onClick={() => handleRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-32 text-center text-gray-500"
                  >
                    No interns found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
        <div className="text-[11px] text-gray-500">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            <CaretLeft className="mr-1" size={12} /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 w-20 bg-white text-[11px]"
          >
            Next <CaretRight className="ml-1" size={12} />
          </Button>
        </div>
      </div>

      <BuildingAssignmentModal
        isOpen={isBuildingModalOpen}
        onClose={() => setIsBuildingModalOpen(false)}
        intern={selectedInternForBuilding}
      />
    </Card>
  );
}
